/**
 * API Configuration
 * 환경별 API 설정을 중앙화하여 관리
 */

// 엔드포인트 설정 타입 정의
interface EndpointConfig {
  [category: string]: {
    [action: string]: string;
  };
}

interface ApiConfig {
  baseURL: string;
  timeout: number;
  endpoints: EndpointConfig;
  // 인증이 필요하지 않은 엔드포인트들
  publicEndpoints: string[];
}

/**
 * 환경별 API 설정
 */
const getApiConfig = (): ApiConfig => {
  const baseURL = getBaseURL();

  return {
    baseURL,
    timeout: 10000,
    endpoints: {
      auth: {
        login: '/api/auth/login',
        logout: '/api/auth/logout',
        register: '/api/auth/register',
        profile: '/api/auth/profile',
        verifyToken: '/api/auth/email-verification/verify-token',
        /** POST body `{ refresh_token }` — axios 401 인터셉터에서 사용. `refreshToken`과 별개. */
        refreshSession: '/api/auth/refresh',
        /** POST, JwtAuthGuard — 현재 액세스 토큰으로 리프레시 토큰 로테이션 */
        refreshToken: '/api/auth/refresh-token',
        changePassword: '/api/auth/change-password',
        deleteAccount: '/api/auth/delete-account',
        sendVerificationLink: '/api/auth/email-verification/send-link',
        verifyEmail: '/api/auth/email-verification/verify-email',
        checkVerificationStatus: '/api/auth/email-verification/check-status',
        updateUserRole: '/api/auth/users/:id/role',
        updateUserProfileById: '/api/auth/users/:id/profile',
        getAllUsers: '/api/auth/users',
        getRegistrationStatus: '/api/auth/email-verification/registration-status',
        requestPasswordReset: '/api/auth/request-password-reset',
        validateResetToken: '/api/auth/validate-reset-token',
        resetPassword: '/api/auth/reset-password',
      },
      allowedEmails: {
        list: '/api/allowed-emails',
        create: '/api/allowed-emails',
        update: '/api/allowed-emails/:id',
        delete: '/api/allowed-emails/:id',
        getById: '/api/allowed-emails/:id',
        check: '/api/allowed-emails/check/:email',
        updateStatus: '/api/allowed-emails/:id/status',
      },
      adminManagement: {
        list: '/api/admin-management',
        getById: '/api/admin-management/:id',
        delete: '/api/admin-management/:id',
      },
      activityLogs: {
        list: '/api/admin/activity-logs',
        usersByCompany: '/api/admin/activity-logs/users/company/:companyId',
        usersBySite: '/api/admin/activity-logs/users/site/:siteId',
      },
      systemStatus: {
        get: '/api/admin/system-status',
      },
      fcmTokens: {
        list: '/api/admin/fcm-tokens',
        delete: '/api/admin/fcm-tokens',
        deleteUser: '/api/admin/fcm-tokens/user',
      },
      company: {
        all: '/api/company/all',
        getById: '/api/company/:id',
        getUserCompany: '/api/company/me/company',
        getCompanyManager: '/api/company/me/company-manager',
        getCompanyManagerById: '/api/company/:id/company-manager',
        update: '/api/company/:id',
        delete: '/api/company/:id',
        getSiteManagers: '/api/company/:companyId/site-managers',
        createSiteManager: '/api/company/:companyId/site-managers',
        updateSiteManager: '/api/company/:companyId/site-managers/:id',
        deleteSiteManager: '/api/company/:companyId/site-managers/:id',
        getSubscription: '/api/company/:id/subscription',
        createSubscription: '/api/company/:id/subscription',
        cancelSubscription: '/api/company/:id/subscription/cancel',
      },
      site: {
        getByCompanyId: '/api/site/company/:companyId',
        create: '/api/site/company/:companyId',
        getUserSite: '/api/site/me/site',
        getMySites: '/api/site/my-sites',
        update: '/api/site/:id',
        delete: '/api/site/:id',
        getSiteInfo: '/api/site/:siteId/info',
      },
      siteArea: {
        getBySiteId: '/api/site/:siteId/area',
        getById: '/api/site/:siteId/area/:areaId',
        create: '/api/site/:siteId/area',
        update: '/api/site/:siteId/area/:areaId',
        delete: '/api/site/:siteId/area/:areaId',
      },
      device: {
        register: '/api/device/register',
        list: '/api/device/list',
        getBySerial: '/api/device/serial/:serialNumber',
        update: '/api/device/:id',
        delete: '/api/device/:id',
        reassign: '/api/device/reassign',
        checkOwnership: '/api/device/check-ownership/:serialNumber',
        saveData: '/api/device/save-data',
        packet: '/api/device/packet',
        getConfigurationAtTimestamp: '/api/device/configuration/at-timestamp',
        forceDisconnect: '/api/device/:serialNumber/force-disconnect',
      },
      liteLogs: {
        createRequest: '/api/device/lite/logs/request',
        updateInfo: '/api/device/lite/logs/:logId/info',
        saveChunk: '/api/device/lite/logs/chunk',
        saveChunkBatch: '/api/device/lite/logs/chunk/batch',
        saveChunkBatchCompressed: '/api/device/lite/logs/chunk/batch/compressed',
        getLogInfo: '/api/device/lite/logs/:logId',
        getLogsByDevice: '/api/device/lite/logs/device/:serialNumber',
        getMissingChunks: '/api/device/lite/logs/:logId/missing-chunks',
        completeLog: '/api/device/lite/logs/:logId/complete',
        failLog: '/api/device/lite/logs/:logId/fail',
        parseLog: '/api/device/lite/logs/:logId/parse',
        gasReadings: '/api/device/lite/logs/:logId/gas-readings',
        gasReadingsByDevice: '/api/device/lite/logs/gas-readings',
        availableDatesByDevice: '/api/device/lite/logs/available-dates',
        calibrationLogs: '/api/device/lite/logs/calibration-logs',
        gasAlarmLogs: '/api/device/lite/logs/gas-alarm-logs',
        eventLogs: '/api/device/lite/logs/event-logs',
        generalConfigLogs: '/api/device/lite/logs/general-config-logs',
        sensorConfigLogs: '/api/device/lite/logs/sensor-config-logs',
        configurationLogs: '/api/device/lite/logs/configuration-logs',
      },
      miniLogs: {
        createRequest: '/api/device/mini/logs/request',
        updateInfo: '/api/device/mini/logs/:logId/info',
        saveChunk: '/api/device/mini/logs/chunk',
        saveChunkBatch: '/api/device/mini/logs/chunk/batch',
        getLogInfo: '/api/device/mini/logs/:logId',
        getLogsByDevice: '/api/device/mini/logs/device/:serialNumber',
        getMissingChunks: '/api/device/mini/logs/:logId/missing-chunks',
        completeLog: '/api/device/mini/logs/:logId/complete',
        failLog: '/api/device/mini/logs/:logId/fail',
        parseLog: '/api/device/mini/logs/:logId/parse',
        gasReadings: '/api/device/mini/logs/:logId/gas-readings',
        gasReadingsByDevice: '/api/device/mini/logs/gas-readings',
        availableDatesByDevice: '/api/device/mini/logs/available-dates',
        calibrationLogs: '/api/device/mini/logs/calibration-logs',
        gasAlarmLogs: '/api/device/mini/logs/gas-alarm-logs',
        eventLogs: '/api/device/mini/logs/event-logs',
        configurationLogs: '/api/device/mini/logs/configuration-logs',
      },
      monitoring: {
        latest: '/api/monitoring/latest',
        history: '/api/monitoring/history',
        availableDates: '/api/monitoring/available-dates',
        pushHistory: '/api/monitoring/push-history',
      },
      dashboard: {
        static: '/api/dashboard/static',
        alarmHistory: '/api/dashboard/alarm-history',
        liveAggregation: '/api/dashboard/live-aggregation',
        report: '/api/dashboard/report',
      },
      fcm: {
        registerWebToken: '/api/fcm/web-token',
        unregisterWebToken: '/api/fcm/web-token/:token',
        unregisterAllWebTokens: '/api/fcm/web-token',
      },
      support: {
        // POST - 고객 문의 접수 (공개: 로그인/회원가입 화면에서도 사용)
        inquiry: '/api/support/inquiry',
      },

      // ====================================
      // 📝 TEMPLATE: 새로운 도메인 추가 시 아래 형식을 따르세요
      // ====================================
      // 복사 방법:
      // 1. 아래 sampleDomain 섹션을 복사
      // 2. 다음을 변경하세요:
      //    - sampleDomain → yourDomain (camelCase)
      //    - sample-domain → your-domain (kebab-case)
      //    - /api/sample-domain → /api/your-domain
      // 3. 엔드포인트명과 URL이 백엔드와 일치하는지 확인
      // ====================================

      /**
       * Sample Domain Endpoints
       * 용법: apiHelpers.get('sampleDomain.list')
       * 관련 파일:
       *   - 백엔드: /apps/backend/src/sample-domain/
       *   - 타입: /packages/types/sample-domain/
       *   - 프론트엔드: /apps/frontend/src/api/sample-domain/useSampleDomain.ts
       */
      sampleDomain: {
        list: '/api/sample-domain', // GET - 목록 조회 (페이지네이션 지원)
        getById: '/api/sample-domain/:id', // GET - 상세 조회
        create: '/api/sample-domain', // POST - 생성
        update: '/api/sample-domain/:id', // PUT - 수정
        delete: '/api/sample-domain/:id', // DELETE - 삭제
      },

      // 참고: 실제 BELLCONNECT 엔드포인트(/api/bellconnect/packet|data|connection)는
      // ApiKeyGuard 기반 외부 앱 전용이라 웹 프론트 apiHelpers로 호출하지 않는다.
      // (과거 여기 있던 /device/:serial/* 항목은 실제 라우트와 무관한 미사용 예시여서 제거함)

      // ====================================
      // 👇 새로운 도메인은 여기에 추가하세요
      // ====================================
      // yourDomain: {
      //   list: '/api/your-domain',
      //   getById: '/api/your-domain/:id',
      //   create: '/api/your-domain',
      //   update: '/api/your-domain/:id',
      //   delete: '/api/your-domain/:id',
      // },
    },
    // 인증이 필요하지 않은 엔드포인트들 (요청 인터셉터가 JWT를 붙이지 않음).
    // 주의: 이 목록은 "프론트가 토큰을 생략하는" 기준일 뿐, 실제 접근 제어는 백엔드 가드가 단일 결정한다.
    // 백엔드에서 가드를 추가/제거하면 이 목록도 함께 동기화해야 한다(이중 관리 지점).
    publicEndpoints: [
      '/api/auth/login',
      '/api/auth/register',
      '/api/auth/email-verification/send-link',
      '/api/auth/email-verification/verify-token',
      '/api/auth/email-verification/verify-email',
      '/api/auth/email-verification/check-status',
      '/api/auth/convert-account',
      '/api/auth/email-verification/registration-status',
      '/api/allowed-emails/check',
      '/api/auth/refresh',
      '/api/auth/refresh-token',
      '/api/auth/logout',
      '/api/auth/request-password-reset',
      '/api/auth/validate-reset-token',
      '/api/auth/reset-password',
      '/api/support/inquiry',
    ],
  };
};

/**
 * 환경에 따른 Base URL 결정
 */
const getBaseURL = (): string => {
  // 환경 변수 디버깅

  // 1. 환경 변수로 직접 지정된 경우 (최우선)
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // 2. 환경 타입으로 지정된 경우
  const apiEnv = import.meta.env.VITE_API_ENV;
  if (apiEnv) {
    const envUrls = {
      local: 'http://localhost:8000',
      dev: 'https://bellcloud-dev.gdi-solution.com', // ← 기본 HTTPS 포트(443) 사용
      temp: 'https://bellcloud-temp.gdi-solution.com', // ← Temp 환경
      production: 'https://bellcloud.gdi-solution.com',
    };

    if (envUrls[apiEnv as keyof typeof envUrls]) {
      return envUrls[apiEnv as keyof typeof envUrls];
    }
  }

  // 3. 개발 환경 (로컬)
  if (import.meta.env.DEV) {
    return 'http://localhost:8000';
  }

  // 4. 현재 도메인에 따라 자동 결정
  const currentHost = window.location.hostname;
  if (currentHost.includes('bellcloud-temp')) {
    return 'https://bellcloud-temp.gdi-solution.com';
  }
  if (currentHost.includes('bellcloud-dev')) {
    return 'https://bellcloud-dev.gdi-solution.com';
  }

  // 5. 기본값 (production)
  return 'https://bellcloud.gdi-solution.com';
};

/**
 * API 설정 인스턴스
 */
export const apiConfig = getApiConfig();

/**
 * 환경 정보 출력 (디버깅용)
 */
export const logApiConfig = () => {};

/**
 * 공개 엔드포인트인지 확인하는 함수
 */
export const isPublicEndpoint = (url: string): boolean => {
  return apiConfig.publicEndpoints.some(endpoint => {
    // 정확한 매치 또는 파라미터가 포함된 경로 매치
    if (endpoint.includes(':')) {
      // 파라미터가 있는 엔드포인트의 경우 패턴 매칭
      const pattern = endpoint.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(url);
    }
    return url.startsWith(endpoint);
  });
};

/**
 * 모든 엔드포인트 목록을 반환하는 함수
 */
export const getAllEndpoints = (): string[] => {
  const endpoints: string[] = [];

  Object.entries(apiConfig.endpoints).forEach(([category, actions]) => {
    Object.keys(actions).forEach(action => {
      endpoints.push(`${category}.${action}`);
    });
  });

  return endpoints;
};

/**
 * 환경별 설정 검증
 */
export const validateApiConfig = (): boolean => {
  try {
    const config = apiConfig;

    // 필수 설정 검증
    if (!config.baseURL) {
      console.error('❌ API baseURL is not configured');
      return false;
    }

    // URL 형식 검증
    try {
      new URL(config.baseURL);
    } catch {
      console.error('❌ Invalid API baseURL format:', config.baseURL);
      return false;
    }

    return true;
  } catch (error) {
    console.error('❌ API configuration validation failed:', error);
    return false;
  }
};
